import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { GoogleIcon } from '../components/GoogleIcon'
import { Logo } from '../components/Logo'
import { Button, Divider, Input, Text } from '../components/ui'
import { authClient } from '../lib/auth-client'

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
})

type LoginForm = z.infer<typeof loginSchema>

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [globalError, setGlobalError] = useState<string | null>(null)
  const [googleLoading, setGoogleLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  async function onSubmit(data: LoginForm) {
    setGlobalError(null)

    const { error } = await authClient.signIn.email({
      email: data.email,
      password: data.password,
    })

    if (error) {
      if (error.status === 403) {
        navigate({ to: '/verify-email', search: { email: data.email } })
        return
      }
      setGlobalError(error.message ?? 'Invalid email or password')
      return
    }

    navigate({ to: '/' })
  }

  async function handleGoogleLogin() {
    setGlobalError(null)
    setGoogleLoading(true)
    try {
      await authClient.signIn.social({
        provider: 'google',
        callbackURL: '/',
      })
    } catch {
      setGlobalError('Could not sign in with Google. Please try again.')
      setGoogleLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-[480px]">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo className="mb-6" />
          <Text variant="h1" className="mb-2">
            Log in to SonderSoft
          </Text>
          <Text variant="body-sm" className="text-foreground/60">
            Enter your account information to continue.
          </Text>
        </div>

        <div className="space-y-4">
          <Button
            variant="outline"
            fullWidth
            size="lg"
            leftIcon={<GoogleIcon />}
            loading={googleLoading}
            onClick={handleGoogleLogin}
          >
            Continue with Google
          </Button>

          <Divider />

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Email"
              type="email"
              placeholder="you@company.com"
              autoComplete="email"
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Your password"
              autoComplete="current-password"
              error={errors.password?.message}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="text-foreground/50 hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
              {...register('password')}
            />

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-foreground/60 underline hover:text-foreground"
              >
                Forgot password?
              </Link>
            </div>

            {globalError && (
              <Text variant="body-sm" className="text-red-600">
                {globalError}
              </Text>
            )}

            <Button type="submit" fullWidth size="lg" loading={isSubmitting}>
              Log in
            </Button>
          </form>
        </div>

        <Text variant="body-sm" className="mt-6 text-center text-foreground/60">
          Don&apos;t have an account?{' '}
          <Link to="/signup" className="font-medium text-foreground underline">
            Sign up
          </Link>
        </Text>
      </div>
    </div>
  )
}
